import { useEffect, useMemo, useState } from "react";
import { AlertCircle, Hash, Lock, Search } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Avatar, Presence } from "./Avatar";
import { PeoplePicker } from "./PeoplePicker";
import { ChannelDetails } from "./ChannelDetails";
import { PreferencesModal } from "./Preferences";
import { Lightbox } from "./Attachments";
import { useChat } from "@/lib/chat";
import { useAuth } from "@/lib/auth";
import { api, errMsg } from "@/lib/api";
import { Mrkdwn, plainText } from "@/lib/mrkdwn";
import { fmtFull } from "@/lib/format";
import { analytics } from "@/lib/analytics";

const Shell = ({ title, sub, onClose, children, testid, wide }) => (
  <Dialog open onOpenChange={(o) => !o && onClose()}>
    <DialogContent className={`tc-dialog p-6 ${wide ? "max-w-[720px]" : "max-w-[520px]"}`} data-testid={testid}>
      <DialogTitle asChild><h2>{title}</h2></DialogTitle>
      <DialogDescription asChild><div className="tc-dialog__sub">{sub || ""}</div></DialogDescription>
      {children}
    </DialogContent>
  </Dialog>
);

const CreateChannelModal = ({ onClose }) => {
  const { createChannel, joinChannel, openConversation, can } = useChat();
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [priv, setPriv] = useState(false);
  const canPrivate = can("channels.create_private");
  const [busy, setBusy] = useState(false);
  const [conflict, setConflict] = useState(null);
  const slug = name.trim().toLowerCase().replace(/[^a-z0-9._-]+/g, "-").replace(/^-|-$/g, "");
  const submit = async (e) => {
    e?.preventDefault();
    if (!slug) return;
    setBusy(true);
    setConflict(null);
    try { await createChannel({ name: slug, description: desc, is_private: priv }); toast.success(`#${slug} created`); onClose(); } catch (err) {
      const d = err?.response?.data?.detail;
      if (d?.code === "name_taken") { setConflict(d); analytics.track("channel_create_conflict_shown", { category: "channels", label: slug, metadata: d.channel }); } else toast.error(errMsg(err));
    } finally { setBusy(false); }
  };
  const open = () => { openConversation(conflict.channel.id); onClose(); };
  const join = async () => {
    setBusy(true);
    try {
      await joinChannel(conflict.channel.id);
      analytics.track("channel_joined_from_conflict", { category: "channels", target: "conversation", target_id: conflict.channel.id, metadata: { is_private: conflict.channel.is_private } });
      toast.success(`You joined #${conflict.channel.name}`);
      open();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Create a channel" sub="Channels are where conversations happen around a topic. Use a name that is easy to find and understand." onClose={onClose} testid="create-channel-modal">
      <form onSubmit={submit} style={{ marginTop: 16 }}>
        {conflict && (
          <div className="tc-conflict" role="alert" data-testid="channel-name-conflict">
            <AlertCircle size={16} />
            <div style={{ flex: 1 }}>
              <b>#{conflict.channel.name} {conflict.channel.is_private ? <Lock size={12} style={{ display: "inline", verticalAlign: -1 }} /> : null}</b> — {conflict.message}.
            </div>
            {conflict.channel.is_member && <button type="button" className="tc-btn tc-btn--outline sm" onClick={open} data-testid="conflict-open">Open</button>}
            {!conflict.channel.is_member && conflict.channel.can_join && <button type="button" className="tc-btn tc-btn--primary sm" onClick={join} disabled={busy} data-testid="conflict-join">{conflict.channel.is_private ? "Join as admin" : "Join channel"}</button>}
          </div>
        )}
        <div className="tc-field">
          <label className="tc-label">Name</label>
          <div style={{ position: "relative" }}>
            <span style={{ position: "absolute", left: 12, top: 0, height: 44, display: "flex", alignItems: "center", color: "var(--tc-text-muted)" }}>{priv ? <Lock size={16} /> : <Hash size={18} />}</span>
            <input className="tc-input" style={{ paddingLeft: 36 }} value={name} onChange={(e) => { setName(e.target.value); setConflict(null); }} placeholder="e.g. plan-budget" maxLength={80} autoFocus data-testid="create-channel-name-input" />
            <span style={{ position: "absolute", right: 12, top: 14, fontSize: 13, color: "var(--tc-text-muted)" }}>{80 - name.length}</span>
          </div>
          {slug && <div className="tc-help">Channel email will be <b>{slug}-xxxx@…</b></div>}
        </div>
        <div className="tc-field">
          <label className="tc-label">Description <span style={{ fontWeight: 400, color: "var(--tc-text-muted)" }}>(optional)</span></label>
          <input className="tc-input sm" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="What's this channel about?" data-testid="create-channel-desc-input" />
        </div>
        <div className="tc-switch-row" style={{ opacity: canPrivate ? 1 : 0.6 }}>
          <div><h4>Make private</h4><p>{canPrivate ? "When a channel is set to private, it can only be viewed or joined by invitation." : "You don't have permission to create private channels."}</p></div>
          <Switch checked={priv} onCheckedChange={setPriv} disabled={!canPrivate} data-testid="create-channel-private-switch" />
        </div>
        <div className="tc-dialog__footer">
          <button type="submit" className="tc-btn tc-btn--primary" disabled={!slug || busy} data-testid="create-channel-submit">Create</button>
        </div>
      </form>
    </Shell>
  );
};

const NewMessageModal = ({ onClose }) => {
  const { openDm } = useChat();
  const [sel, setSel] = useState([]);
  const go = async () => { try { await openDm(sel); onClose(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Shell title="New message" sub="Start a direct message with one person or a group of up to 8 people." onClose={onClose} testid="new-message-modal">
      <div style={{ marginTop: 16 }}><PeoplePicker selected={sel} onChange={setSel} placeholder="To: @somebody or somebody@example.com" /></div>
      <div className="tc-dialog__footer"><button className="tc-btn tc-btn--primary" disabled={!sel.length} onClick={go} data-testid="new-message-go">Go</button></div>
    </Shell>
  );
};

const BrowseChannelsModal = ({ onClose }) => {
  const { joinChannel, leaveChannel, openConversation, meId, setModal } = useChat();
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const load = () => api.get("/conversations/browse").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const shown = list.filter((c) => !q || c.name.includes(q.toLowerCase()));
  return (
    <Shell title="Browse channels" sub={`${list.length} channels`} onClose={onClose} testid="browse-channels-modal" wide>
      <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
        <input className="tc-input sm" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search channels" autoFocus data-testid="browse-search-input" />
        <button className="tc-btn tc-btn--primary" onClick={() => setModal({ type: "createChannel" })} data-testid="browse-create-channel">Create channel</button>
      </div>
      <div className="tc-detail-block tc-scroll" style={{ maxHeight: 420 }}>
        {shown.map((c) => {
          const member = c.members.includes(meId);
          return (
            <div key={c.id} className="tc-channel-row" data-testid={`browse-row-${c.name}`}>
              <div className="info">
                <button className="name" onClick={() => { openConversation(c.id); onClose(); }}>{c.is_private ? <Lock size={14} /> : <Hash size={15} />} {c.name} {member && <span className="tc-pill" style={{ marginLeft: 6 }}>Joined</span>}</button>
                <div className="desc">{c.member_count} members{c.description ? ` · ${c.description}` : c.topic ? ` · ${c.topic}` : ""} · {c.email}</div>
              </div>
              {member ? (
                <button className="tc-btn tc-btn--outline sm" onClick={() => { openConversation(c.id); onClose(); }} data-testid={`browse-view-${c.name}`}>View</button>
              ) : (
                <button className="tc-btn tc-btn--primary sm" onClick={async () => { await joinChannel(c.id); load(); toast.success(`Joined #${c.name}`); }} data-testid={`browse-join-${c.name}`}>Join</button>
              )}
              {member && !c.is_default && <button className="tc-btn tc-btn--ghost sm" onClick={() => leaveChannel(c.id).then(load)} data-testid={`browse-leave-${c.name}`}>Leave</button>}
            </div>
          );
        })}
      </div>
    </Shell>
  );
};

const ForwardModal = ({ message, onClose }) => {
  const { conversations, forwardMessage, openConversation } = useChat();
  const [target, setTarget] = useState(null);
  const [comment, setComment] = useState("");
  const [q, setQ] = useState("");
  const list = useMemo(() => conversations.filter((c) => !q || (c.display_name || "").toLowerCase().includes(q.toLowerCase())).slice(0, 12), [conversations, q]);
  const send = async () => {
    try { await forwardMessage(message.id, target.id, comment); toast.success(`Forwarded to ${target.type === "channel" ? "#" : ""}${target.display_name}`); onClose(); openConversation(target.id); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Shell title="Forward this message" onClose={onClose} testid="forward-modal">
      <div style={{ marginTop: 12 }}>
        <input className="tc-input sm" placeholder="Search for channel or person" value={target ? (target.type === "channel" ? `#${target.display_name}` : target.display_name) : q} onChange={(e) => { setTarget(null); setQ(e.target.value); }} autoFocus data-testid="forward-search-input" />
        {!target && (
          <div className="tc-people-list tc-scroll" style={{ marginTop: 8, maxHeight: 200 }}>
            {list.map((c) => <button key={c.id} className="tc-people-row" onClick={() => setTarget(c)} data-testid={`forward-target-${c.id}`}>{c.type === "channel" ? (c.is_private ? <Lock size={14} /> : <Hash size={15} />) : <Avatar user={c.members_info?.[0]} size={20} radius={4} />}<span>{c.display_name}</span></button>)}
          </div>
        )}
        <textarea className="tc-textarea" style={{ marginTop: 12 }} placeholder="Add a message, if you'd like." value={comment} onChange={(e) => setComment(e.target.value)} data-testid="forward-comment-input" />
        <div className="tc-forward" style={{ marginTop: 12 }}>
          <div className="tc-msg__head"><Avatar user={message.user} size={20} radius={4} /><span className="tc-msg__author" style={{ fontSize: 14 }}>{message.user?.display_name}</span><span className="tc-msg__time">{fmtFull(message.created_at)}</span></div>
          <Mrkdwn text={message.text} />
        </div>
      </div>
      <div className="tc-dialog__footer"><button className="tc-btn tc-btn--outline" onClick={onClose}>Cancel</button><button className="tc-btn tc-btn--primary" disabled={!target} onClick={send} data-testid="forward-submit">Forward</button></div>
    </Shell>
  );
};

const EditProfileModal = ({ onClose }) => {
  const { user, setUser } = useAuth();
  const [f, setF] = useState({ display_name: user.display_name || "", real_name: user.real_name || "", title: user.title || "", phone: user.phone || "", status_text: user.status_text || "", status_emoji: user.status_emoji || "", avatar_url: user.avatar_url || "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    try { const { data } = await api.patch("/users/me", f); setUser(data); toast.success("Profile updated"); onClose(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Shell title="Edit your profile" onClose={onClose} testid="edit-profile-modal">
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginTop: 16 }}>
        <div className="tc-field" style={{ margin: 0 }}><label className="tc-label">Full name</label><input className="tc-input sm" value={f.real_name} onChange={set("real_name")} data-testid="profile-real-name-input" /></div>
        <div className="tc-field" style={{ margin: 0 }}><label className="tc-label">Display name</label><input className="tc-input sm" value={f.display_name} onChange={set("display_name")} data-testid="profile-display-name-input" /></div>
        <div className="tc-field" style={{ margin: 0 }}><label className="tc-label">Title</label><input className="tc-input sm" value={f.title} onChange={set("title")} placeholder="Title" data-testid="profile-title-input" /></div>
        <div className="tc-field" style={{ margin: 0 }}><label className="tc-label">Phone</label><input className="tc-input sm" value={f.phone} onChange={set("phone")} placeholder="(555) 123-4567" data-testid="profile-phone-input" /></div>
        <div className="tc-field" style={{ margin: 0 }}><label className="tc-label">Status emoji</label><input className="tc-input sm" value={f.status_emoji} onChange={set("status_emoji")} placeholder="💬" data-testid="profile-status-emoji-input" /></div>
        <div className="tc-field" style={{ margin: 0 }}><label className="tc-label">Status</label><input className="tc-input sm" value={f.status_text} onChange={set("status_text")} placeholder="What's your status?" data-testid="profile-status-input" /></div>
        <div className="tc-field" style={{ margin: 0, gridColumn: "1 / -1" }}><label className="tc-label">Profile photo URL</label><input className="tc-input sm" value={f.avatar_url} onChange={set("avatar_url")} placeholder="https://…" data-testid="profile-avatar-input" /></div>
      </div>
      <div className="tc-help">Your Total Chat email address is <b>{user.chat_email}</b> and can't be changed here.</div>
      <div className="tc-dialog__footer"><button className="tc-btn tc-btn--outline" onClick={onClose}>Cancel</button><button className="tc-btn tc-btn--primary" onClick={save} data-testid="profile-save-button">Save changes</button></div>
    </Shell>
  );
};

const SearchModal = ({ scope, onClose }) => {
  const { openConversation, openProfile, openThread, usersByHandle, usersById, meId } = useChat();
  const { workspace } = useAuth();
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!q.trim()) { setRes(null); return undefined; }
    const t = setTimeout(async () => {
      setBusy(true);
      try { const { data } = await api.get("/search", { params: { q } }); setRes(data); analytics.track("search_results_viewed", { category: "search", label: q, metadata: { results: data.messages.length } }); } finally { setBusy(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);
  const msgs = (res?.messages || []).filter((m) => !scope || m.conversation_id === scope.id);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="tc-dialog p-0 max-w-[720px] overflow-hidden" data-testid="search-modal">
        <DialogTitle className="sr-only">Search</DialogTitle>
        <DialogDescription className="sr-only">Search messages, channels and people</DialogDescription>
        <div className="tc-search-bar"><Search size={20} style={{ color: "var(--tc-text-muted)" }} /><input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={scope ? `Search in ${scope.type === "channel" ? "#" : ""}${scope.display_name}` : `Search ${workspace?.name}`} data-testid="search-input" />{busy && <span style={{ fontSize: 12, color: "var(--tc-text-muted)" }}>Searching…</span>}</div>
        <div className="tc-search-results tc-scroll">
          {!res && <div style={{ padding: "20px 16px", color: "var(--tc-text-muted)", fontSize: 13 }}>Search for messages, channels and people. Try <span className="tc-kbd">morning</span> or a teammate's name.</div>}
          {res && !res.channels.length && !res.users.length && !msgs.length && <div className="tc-empty" style={{ padding: 40 }}><h3>No results</h3><p>Nothing matched "{q}".</p></div>}
          {res?.channels.length > 0 && !scope && <div className="tc-search-group"><h5>Channels</h5>{res.channels.map((c) => <button key={c.id} className="tc-search-item" onClick={() => { openConversation(c.id); onClose(); }} data-testid={`search-channel-${c.name}`}>{c.is_private ? <Lock size={15} /> : <Hash size={16} />}<span>{c.name}</span><span className="meta">{c.member_count} members · {c.email}</span></button>)}</div>}
          {res?.users.length > 0 && !scope && <div className="tc-search-group"><h5>People</h5>{res.users.map((u) => <button key={u.id} className="tc-search-item" onClick={() => { openProfile(u.id); onClose(); }} data-testid={`search-user-${u.handle}`}><Avatar user={u} size={24} radius={5} /><span>{u.display_name}</span><Presence presence={u.presence} /><span className="meta">@{u.handle} · {u.chat_email}</span></button>)}</div>}
          {msgs.length > 0 && <div className="tc-search-group"><h5>Messages</h5>{msgs.map((m) => (
            <button key={m.id} className="tc-search-item" style={{ alignItems: "flex-start" }} onClick={() => { openConversation(m.conversation_id); if (m.parent_id) openThread(m.parent_id, m.conversation_id); onClose(); }} data-testid={`search-message-${m.id}`}>
              <Avatar user={m.user} size={24} radius={5} />
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ display: "flex", gap: 8, alignItems: "baseline" }}><b style={{ fontSize: 14 }}>{m.user?.display_name}</b><span className="meta">{m.conversation?.type === "channel" ? `#${m.conversation.name}` : m.conversation?.display_name} · {fmtFull(m.created_at)}</span></div>
                <div className="snippet">{plainText(m.text)}</div>
              </div>
            </button>
          ))}</div>}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const HelpModal = ({ onClose }) => (
  <Shell title="Help & keyboard shortcuts" onClose={onClose} testid="help-modal">
    <div style={{ marginTop: 12, fontSize: 15, display: "grid", gap: 8 }}>
      {[["Enter", "Send message"], ["Shift + Enter", "New line"], ["⌘/Ctrl + B", "Bold"], ["⌘/Ctrl + I", "Italic"], ["⌘/Ctrl + U", "Underline"], ["⌘/Ctrl + Shift + X", "Strikethrough"], ["@", "Mention someone, @channel or @here"], [":", "Emoji shortcodes like :tada:"]].map(([k, v]) => (
        <div key={k} style={{ display: "flex", justifyContent: "space-between" }}><span>{v}</span><span className="tc-kbd">{k}</span></div>
      ))}
    </div>
  </Shell>
);

export const Modals = () => {
  const { modal, setModal, openProfile } = useChat();
  const close = () => setModal(null);
  useEffect(() => { if (modal?.type === "profile") { openProfile(modal.userId); setModal(null); } }, [modal, openProfile, setModal]);
  if (!modal) return null;
  switch (modal.type) {
    case "createChannel": return <CreateChannelModal onClose={close} />;
    case "newMessage": return <NewMessageModal onClose={close} />;
    case "browseChannels": return <BrowseChannelsModal onClose={close} />;
    case "forward": return <ForwardModal message={modal.message} onClose={close} />;
    case "editProfile": return <EditProfileModal onClose={close} />;
    case "search": return <SearchModal scope={modal.scope} onClose={close} />;
    case "details": return <ChannelDetails conversationId={modal.conversationId} initialTab={modal.tab} onClose={close} />;
    case "help": return <HelpModal onClose={close} />;
    case "preferences": return <PreferencesModal initialTab={modal.tab} onClose={close} />;
    case "lightbox": return <Lightbox files={modal.files} index={modal.index} onClose={close} />;
    default: return null;
  }
};
